import React, { useState } from 'react';
import { X, Loader2, AlertCircle, UserPlus } from 'lucide-react';
import { atlasApi, AtlasUser, NovoColaboradorManual } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  onClose: () => void;
  onCriado: (funccodi: number) => void;
}

const Campo: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}> = ({ label, value, onChange, type = 'text', placeholder, required, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </span>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={ev => onChange(ev.target.value)}
      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
    />
  </label>
);

const ModalNovoColaborador: React.FC<Props> = ({ user, onClose, onCriado }) => {
  const [form, setForm] = useState<NovoColaboradorManual>({ nome: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const upd = <K extends keyof NovoColaboradorManual>(k: K, v: NovoColaboradorManual[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const salvar = async () => {
    if (!form.nome.trim()) {
      setErro('Nome é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const doc = await atlasApi.criarColaboradorManual(user, form);
      onCriado(doc.funccodi);
    } catch (e: any) {
      setErro(e.message || 'Erro ao criar colaborador.');
      setSalvando(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        onClick={ev => ev.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
            <UserPlus className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900">Novo colaborador</h3>
            <p className="text-xs text-gray-400">PJ, terceirizado ou estagiário (fora do GIX).</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Campo label="Nome completo" value={form.nome || ''} onChange={v => upd('nome', v)} required />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo label="Matrícula" value={form.matricula || ''} onChange={v => upd('matricula', v)} placeholder="opcional" />
            <Campo label="E-mail corporativo" value={form.email_corporativo || ''} onChange={v => upd('email_corporativo', v)} type="email" />
          </div>
          <Campo label="Cargo" value={form.cargo_nome || ''} onChange={v => upd('cargo_nome', v)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo label="Centro de custo / Setor" value={form.centro_custo_nome || ''} onChange={v => upd('centro_custo_nome', v)} placeholder="ex: TECNOLOGIA" />
            <Campo label="Departamento (código)" value={form.departamento_codigo || ''} onChange={v => upd('departamento_codigo', v)} placeholder="opcional" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo label="Data de admissão" value={form.data_admissao || ''} onChange={v => upd('data_admissao', v)} type="date" />
            <Campo label="Data de nascimento" value={form.data_nascimento || ''} onChange={v => upd('data_nascimento', v)} type="date" />
          </div>

          {erro && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4" /> {erro}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={salvando || !form.nome.trim()}
              className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-2"
            >
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Criar colaborador
            </button>
          </div>

          <p className="text-[11px] text-gray-400">
            O colaborador será marcado como <strong>origem manual</strong> e não será afetado pela sincronização do GIX.
            Você pode definir o gestor depois, na ficha do colaborador.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModalNovoColaborador;
